/* eslint-disable react/jsx-props-no-spreading */
/**
 * PowerBIReportInstanceModal - Modal component for creating and editing PBI Report Instance records.
 *
 * This component provides a form interface for managing PBI Report Instance records.
 * It follows the PowerBIReportModal.tsx pattern using react-hook-form with FormProvider/Controller,
 * ResponsiveModal wrapper, and consistent validation.
 *
 * Features:
 * - Dropdown for selecting parent PBIReportDefinition
 * - Conditional ClientId field visibility based on parent's IsClientLevelAuthorization flag
 * - GUID validation for workspace and object IDs
 * - Form validation with react-hook-form
 * - Create and Edit modes with appropriate API calls
 *
 * @module AdminSettings/PowerBIReportInstanceModal
 */
import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, IconButton, Select, styled } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CircleErrorIcon, CloseIcon } from '@rsmus/myrsm-icons'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import {
  PbiReportDefinitionViewModel,
  PbiReportInstanceViewModel,
} from '@rsmus/ecp-userservice'
import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import TextField from '../TextField/TextField'
import { tokens } from '../../styles/materialTheme'

/**
 * Extended AdminReportDataViewModel with isClientLevelAuthorization property.
 * Used for type safety when checking parent definition's authorization model.
 * This property will be available from @rsmus/ecp-userservice after TypeScript client regeneration.
 */
type ExtendedAdminReportDataViewModel = PbiReportDefinitionViewModel

/**
 * Mode type for the modal - determines create vs edit behavior
 */
type Mode = 'create' | 'edit'

/**
 * Props interface for PowerBIReportInstanceModal component
 */
interface PowerBIReportInstanceModalProps {
  /** Controls whether the modal is visible */
  open: boolean
  /** Callback invoked when the modal is closed */
  onClose: () => void
  /** Callback invoked when a new instance is saved (create mode) */
  onSave?: (instance: PbiReportInstanceViewModel) => void
  /** Mode of operation: 'create' for new instances, 'edit' for existing */
  mode?: Mode
  /** Initial data for edit mode */
  initialData?: PbiReportInstanceViewModel
  /** Callback invoked when an instance is updated (edit mode) */
  onUpdate?: (instance: PbiReportInstanceViewModel) => void
  /** Callback invoked when an instance is removed (edit mode) */
  onRemove?: (instanceId: number) => void
  /** List of available parent report definitions for the dropdown */
  reportDefinitions?: ExtendedAdminReportDataViewModel[]
}

/**
 * Styles for the modal component following the PowerBIReportModal pattern
 */
const styles: Styles = {
  container: (theme) => ({
    padding: '1.5rem',
    [theme.breakpoints.down('tablet')]: {
      paddingBottom: '1.5rem',
    },
  }),
  fieldContainer: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      gridTemplateColumns: '1fr',
    },
  }),
  title: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    paddingBottom: '1.75rem',
    border: '0',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '2rem',
      paddingTop: '1rem',
    },
  }),
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexFlow: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '1.5rem',
    gap: '0.625rem',
    border: '0',
  },
  errorHeader: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBold,
    fontSize: '1.125rem',
    lineHeight: '1.75rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.5rem',
      paddingTop: '1rem',
    },
  }),
  errorMessage: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1rem',
      paddingBottom: '1rem',
    },
  }),
  errorContainer: {
    width: '100%',
    height: '7.8125rem',
    padding: '1.5rem',
    backgroundColor: tokens.colors.warning.secondary,
    marginBottom: '1.5rem',
  },
}

/**
 * Styled Select field component for dropdowns
 */
const StyledSelectField = styled(Select)({
  width: '100%',
  '.MuiOutlinedInput-root': {
    width: '100%',
    maxWidth: '100%',
  },
  '.MuiSelect-select': {
    fontFamily: 'Prelo-Book',
  },
})

/**
 * Styled Label component for form field labels
 */
const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

/**
 * PowerBIReportInstanceModal - Modal component for creating and editing PBI Report Instance records.
 *
 * @param props - Component props
 * @returns React component for PBI Report Instance management
 */
const PowerBIReportInstanceModal = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  initialData,
  onUpdate,
  onRemove,
  reportDefinitions = [],
}: PowerBIReportInstanceModalProps) => {
  const { t } = useTranslation()

  // Form state management with react-hook-form
  const methods = useForm({
    defaultValues: {
      pbiReportDefinitionId: '',
      displayName: '',
      clientId: '',
      cemRoleName: '',
      isDashboard: 'false',
      pbiWorkspaceId: '',
      pbiObjectId: '',
    },
  })

  // Component state
  const [hasError, setHasError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // Validation regex patterns (same as PowerBIReportModal)
  const letterNumberHyphenRegex = /^[A-Za-z0-9\- ()]+$/
  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

  /**
   * Formats a GUID string to uppercase and trims whitespace
   * @param value - The GUID string to format
   * @returns Formatted GUID string
   */
  const formatGUID = (value: string) => value.toUpperCase().trim()

  /**
   * Watch the selected report definition ID to determine ClientId visibility
   */
  const watchedDefinitionId = methods.watch('pbiReportDefinitionId')

  /**
   * Get the selected report definition based on the watched ID
   */
  const selectedDefinition = reportDefinitions.find(
    (def) => def.id?.toString() === watchedDefinitionId,
  )

  /**
   * Determine if ClientId field should be shown and required
   * Based on the parent definition's IsClientLevelAuthorization flag
   */
  const defAny = selectedDefinition as any

  const isClientLevelAuthRequired =
    defAny?.isClientLevelAuthorization === true ||
    defAny?.IsClientLevelAuthorization === true

  useEffect(() => {
    if (!isClientLevelAuthRequired) {
      methods.setValue('clientId', '')
      methods.clearErrors('clientId')
    }
  }, [isClientLevelAuthRequired, methods])

  /**
   * Reset form state when modal opens/closes or mode/initialData changes
   */
  useEffect(() => {
    if (!open) return
    setHasError(false)
    setIsSaving(false)
    methods.clearErrors()

    if (mode !== 'edit' || !initialData) {
      methods.reset()
      return
    }

    // Helper function to safely extract values from initialData
    const pick = (obj: Record<string, unknown>, ...keys: string[]) =>
      keys.reduce<unknown>((val, k) => val ?? obj?.[k], undefined)

    // Populate form with initial data for edit mode
    // Cast initialData to unknown first to avoid TypeScript overlap error
    const dataRecord = initialData as unknown as Record<string, unknown>
    methods.reset({
      pbiReportDefinitionId: String(
        pick(dataRecord, 'pbiReportDefinitionId') ?? '',
      ),
      displayName: String(pick(dataRecord, 'displayName') ?? ''),
      clientId: String(pick(dataRecord, 'clientId') ?? ''),
      cemRoleName: String(pick(dataRecord, 'cemRoleName') ?? ''),
      isDashboard: String(!!pick(dataRecord, 'isDashboard')),
      pbiWorkspaceId: String(pick(dataRecord, 'pbiWorkspaceId') ?? ''),
      pbiObjectId: String(pick(dataRecord, 'pbiObjectId') ?? ''),
    })
  }, [open, mode, initialData, methods])

  /**
   * Handle creating a new report instance.
   * Uses axios directly until TypeScript client is regenerated with report_AddReportInstance method.
   */
  const handleAddNewInstance = async () => {
    setHasError(false)
    setIsSaving(true)
    const isDashboard = methods.getValues('isDashboard') === 'true'

    // Build the instance object for API call
    const instance = {
      id: 0,
      pbiReportDefinitionId: parseInt(
        methods.getValues('pbiReportDefinitionId'),
        10,
      ),
      displayName: methods.getValues('displayName').trim(),
      clientId: isClientLevelAuthRequired
        ? methods.getValues('clientId').trim()
        : null,
      cemRoleName: methods.getValues('cemRoleName').trim(),
      isDashboard,
      pbiWorkspaceId: formatGUID(methods.getValues('pbiWorkspaceId')),
      pbiObjectId: formatGUID(methods.getValues('pbiObjectId')),
    } as PbiReportInstanceViewModel

    try {
      // Call API endpoint directly via axios until TypeScript client is regenerated
      await api.user.report_AddReportInstance(
        instance as PbiReportInstanceViewModel,
      )
      methods.reset()
      onSave?.(instance)
    } catch (error: unknown) {
      // Handle axios error response
      const apiError = error as { response?: { data?: { message?: string } } }
      const containsDisplayNameError = apiError?.response?.data?.message
        ? apiError.response.data.message.includes(
            'Display name must be unique.',
          )
        : false
      if (containsDisplayNameError) {
        methods.setError('displayName', {
          message: t('PowerBI.ReportModal.DisplayNameMustBeUnique'),
        })
      } else {
        setHasError(true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Handle updating an existing report instance.
   * Uses axios directly until TypeScript client is regenerated with report_UpdateReportInstance method.
   */
  const handleUpdateInstance = async () => {
    if (!initialData) return
    setHasError(false)
    setIsSaving(true)
    const isDashboard = methods.getValues('isDashboard') === 'true'

    // Build the instance object with updated values
    const instance = {
      ...initialData,
      pbiReportDefinitionId: parseInt(
        methods.getValues('pbiReportDefinitionId'),
        10,
      ),
      displayName: methods.getValues('displayName').trim(),
      clientId: isClientLevelAuthRequired
        ? methods.getValues('clientId').trim()
        : null,
      cemRoleName: methods.getValues('cemRoleName').trim(),
      isDashboard,
      pbiWorkspaceId: formatGUID(methods.getValues('pbiWorkspaceId')),
      pbiObjectId: formatGUID(methods.getValues('pbiObjectId')),
    } as PbiReportInstanceViewModel

    try {
      // Call API endpoint directly via axios until TypeScript client is regenerated
      await api.user.report_UpdateReportInstance(
        instance as PbiReportInstanceViewModel,
      )
      methods.reset()
      onUpdate?.(instance)
    } catch (error: unknown) {
      // Handle axios error response
      const apiError = error as { response?: { data?: { message?: string } } }
      const containsDisplayNameError = apiError?.response?.data?.message
        ? apiError.response.data.message.includes(
            'Display name must be unique.',
          )
        : false
      if (containsDisplayNameError) {
        methods.setError('displayName', {
          message: t('PowerBI.ReportModal.DisplayNameMustBeUnique'),
        })
      } else {
        setHasError(true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * Handle modal close - reset form and close
   */
  const handleOnClose = () => {
    methods.reset()
    setHasError(false)
    onClose()
  }

  /**
   * Handle keyboard interaction for close icon
   */
  const handleCloseIconKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (handleOnClose && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault()
        handleOnClose()
      }
    },
    [handleOnClose],
  )

  /**
   * Validate GUID format for workspace and object IDs
   * @param value - The value to validate
   * @returns Error message or true if valid
   */
  const guidValidation = (value: string) => {
    if (!guidRegex.test(value.trim().toUpperCase())) {
      return t('PowerBI.ReportModal.InvalidGUIDError')
    }
    return true
  }

  /**
   * Validate string fields against a regex pattern
   * @param value - The value to validate
   * @param errorText - Error message to return if invalid
   * @param regex - Regex pattern to test against
   * @returns Error message or true if valid
   */
  const stringValidation = (
    value: string,
    errorText: string,
    regex: RegExp,
  ) => {
    if (!regex.test(value)) {
      return errorText
    }
    return true
  }

  /**
   * Render the IsDashboard select field
   */
  const renderIsDashboardSelectField = () => (
    <Box>
      <StyledLabel htmlFor="isDashboard">
        {t('PowerBI.ReportInstanceModal.IsDashboard')} *
        <span className="sr-only">{t('srOnlyRequired')}</span>
      </StyledLabel>
      <Controller
        name="isDashboard"
        control={methods.control}
        defaultValue="false"
        render={({ field: { onChange, value, ref } }) => (
          <StyledSelectField
            native
            id="isDashboard"
            inputRef={ref}
            value={value}
            onChange={onChange}
            inputProps={{ 'aria-describedby': 'isDashboardError' }}>
            <option value="false">{t('PowerBI.ReportModal.No')}</option>
            <option value="true">{t('PowerBI.ReportModal.Yes')}</option>
          </StyledSelectField>
        )}
      />
    </Box>
  )

  /**
   * Render the Report Definition dropdown field
   */
  const renderReportDefinitionSelectField = () => (
    <Box>
      <StyledLabel htmlFor="pbiReportDefinitionId">
        {t('PowerBI.ReportInstanceModal.ReportDefinition')} *
        <span className="sr-only">{t('srOnlyRequired')}</span>
      </StyledLabel>
      <Controller
        name="pbiReportDefinitionId"
        control={methods.control}
        rules={{
          required: `${t('PowerBI.ReportInstanceModal.ReportDefinition')} ${t('IsARequiredField')}`,
        }}
        render={({
          field: { onChange, value, ref },
          fieldState: { error },
        }) => (
          <>
            <StyledSelectField
              native
              id="pbiReportDefinitionId"
              inputRef={ref}
              value={value}
              onChange={onChange}
              error={!!error}
              disabled={mode === 'edit'}
              inputProps={{ 'aria-describedby': 'pbiReportDefinitionIdError' }}
              data-testid="Sel_PowerBIReportInstanceModal_ReportDefinition">
              <option value="">{t('PowerBI.ReportModal.Placeholder')}</option>
              {reportDefinitions.map((def) => (
                <option key={def.id} value={def.id?.toString()}>
                  {def.reportName}
                </option>
              ))}
            </StyledSelectField>
            {error && (
              <Box
                component="span"
                sx={{
                  display: 'block',
                  color: tokens.colors.rsmRed.secondary,
                  fontFamily: 'Prelo-Book, sans-serif',
                  fontSize: '.875rem',
                  paddingTop: '0.25rem',
                }}>
                {error.message}
              </Box>
            )}
          </>
        )}
      />
    </Box>
  )

  // Determine modal title based on mode
  const title =
    mode === 'create'
      ? t('PowerBI.ReportInstanceModal.Header')
      : t('PowerBI.ReportInstanceModal.EditHeader')

  // Determine primary button label based on mode
  const primaryLabel =
    mode === 'create'
      ? t('PowerBI.ReportInstanceModal.AddReportInstance')
      : t('PowerBI.ReportInstanceModal.UpdateReportInstance')

  // Determine submit handler based on mode
  const onSubmit =
    mode === 'create'
      ? () => handleAddNewInstance()
      : () => handleUpdateInstance()

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={handleOnClose}
        aria-label={title}
        desktopMaxHeight="40rem"
        removeHeaderSpacing
        removeCloseButton
        removeContentSpacing
        data-testid="Lbl_PowerBIReportInstance_AddNewDialog_Modal">
        <FormProvider {...methods}>
          <Box
            component="form"
            sx={styles.container}
            onSubmit={methods.handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                sx={{ padding: 0 }}
                tabIndex={0}
                aria-label={t('ModalDialog.CloseDialog')}
                data-testid="Btn_Modal_Close"
                onClick={handleOnClose}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleCloseIconKeyDown(event)
                }
                disableFocusRipple
                disableRipple>
                <CloseIcon size={12.5} />
              </IconButton>
            </Box>
            <Box component="h1" sx={styles.title}>
              {title}
            </Box>
            {hasError && (
              <Box sx={styles.errorContainer}>
                <Box
                  sx={{
                    alignItems: 'center',
                    height: '100%',
                    display: 'flex',
                  }}>
                  <CircleErrorIcon
                    size="4rem"
                    color={tokens.colors.rsmGray.copy}
                    fillColor
                  />
                  <Box sx={{ paddingLeft: '1.5rem' }}>
                    <Box
                      component="span"
                      className="title"
                      sx={styles.errorHeader}
                      data-testid="ReportInstanceModal_Error_Header">
                      {t('PowerBI.ReportModal.ErrorHeader')}
                    </Box>
                    <Box
                      sx={styles.errorMessage}
                      data-testid="ReportInstanceModal_Error_Message">
                      {t('PowerBI.ReportModal.ErrorMessage')}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={styles.fieldContainer}>
              {renderReportDefinitionSelectField()}
              <TextField
                labelName={t('PowerBI.ReportInstanceModal.DisplayName')}
                fieldName="displayName"
                componentName="PowerBIReportInstanceModal"
                maxLength={200}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
              />
              <TextField
                labelName={t('PowerBI.ReportInstanceModal.CEMRoleName')}
                fieldName="cemRoleName"
                componentName="PowerBIReportInstanceModal"
                maxLength={50}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
              />
              {isClientLevelAuthRequired && (
                <TextField
                  labelName={t('PowerBI.ReportInstanceModal.ClientId')}
                  fieldName="clientId"
                  componentName="PowerBIReportInstanceModal"
                  maxLength={50}
                  fieldRequired
                  placeholderText={t('PowerBI.ReportModal.Placeholder')}
                />
              )}
              {renderIsDashboardSelectField()}
              <TextField
                labelName={t('PowerBI.ReportInstanceModal.PBIWorkspaceId')}
                fieldName="pbiWorkspaceId"
                componentName="PowerBIReportInstanceModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
              <TextField
                labelName={t('PowerBI.ReportInstanceModal.PBIObjectId')}
                fieldName="pbiObjectId"
                componentName="PowerBIReportInstanceModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
            </Box>
            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                data-testid="Btn_AddNewInstanceDialog_Cancel"
                disabled={isSaving}
                onClick={handleOnClose}>
                {t('PowerBI.ReportInstanceModal.Cancel')}
              </Button>
              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() => initialData && onRemove?.(initialData.id)}
                  data-testid="Btn_EditInstanceDialog_RemoveInstance">
                  {t('PowerBI.ReportModal.RemoveReport')}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                data-testid={
                  mode === 'create'
                    ? 'Btn_AddNewInstanceDialog_AddInstance'
                    : 'Btn_EditInstanceDialog_Save'
                }>
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </ResponsiveModal>
    </Box>
  )
}

export default PowerBIReportInstanceModal
